import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { fetchSignWordCandidates } from "../lib/sign-words-api";

const DEBOUNCE_MS = 200;

const useDebouncedValue = (value: string, delayMs: number): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

type SignWordSearchProps = {
  /** 最初から入っている検索語（再生画面から戻ってきたときの復元用） */
  initialQuery?: string;
  /** 検索語が変わるたびに呼ばれる（URL への保存用） */
  onQueryChange?: (query: string) => void;
};

/** 打つたびに手話単語の候補が出る検索欄。候補を押しても入力欄のフォーカスは外れない */
export const SignWordSearch = ({
  initialQuery = "",
  onQueryChange,
}: SignWordSearchProps) => {
  const [input, setInput] = useState(initialQuery);
  const debounced = useDebouncedValue(input, DEBOUNCE_MS);
  const enabled = debounced.trim() !== "";

  const query = useQuery({
    queryKey: ["sign-word-candidates", debounced],
    queryFn: () => fetchSignWordCandidates(debounced),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const showEmpty =
    enabled &&
    query.isSuccess &&
    !query.isPlaceholderData &&
    query.data.length === 0;

  return (
    <div className="space-y-3">
      <input
        type="search"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          onQueryChange?.(e.target.value);
        }}
        placeholder="ことばをいれる（例: ほいくえん）"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        enterKeyHint="search"
        className="w-full rounded-xl border border-neutral-300 bg-white p-3 text-base"
      />

      {enabled && query.isError && (
        <p className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          検索に失敗しました。通信を確かめて、もう一度ためしてください。
        </p>
      )}

      {showEmpty && <p className="text-sm text-neutral-500">見つかりません</p>}

      {enabled && query.data && query.data.length > 0 && (
        <ul className="divide-y divide-neutral-200 rounded-2xl border border-neutral-200 bg-white">
          {query.data.map((c) => (
            <li key={`${c.code}-${c.title}`}>
              <Link
                to="/sign-words/$code"
                params={{ code: c.code }}
                // 押した瞬間に入力欄からフォーカスが外れ、キーボードが閉じるのを防ぐ
                onMouseDown={(e) => e.preventDefault()}
                className="flex flex-col px-4 py-3 hover:bg-neutral-50"
              >
                <span className="font-semibold">
                  {c.variant ? `${c.title} (${c.variant})` : c.title}
                </span>
                <span className="text-xs text-neutral-500">{c.ruby}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
